// src/components/DeviceTerminal.tsx
//
// A console for one device, for Pinage staff.
//
// ============================================================================
// THIS IS A COMMAND CONSOLE, NOT A SHELL
// ============================================================================
// The device runs each command with subprocess.run(shell=True) and reports the
// output when it finishes. There is no pseudo-terminal on the other end, and
// that shapes what this can honestly offer:
//
//   - every command runs in a fresh shell, so `cd` does not persist and
//     neither does an exported variable
//   - nothing interactive works. top, vim, less and anything that waits for a
//     keypress will sit there until the device's 30 second limit kills it
//   - there is no Ctrl-C to send, because there is no process attached to us
//
// What it is good for is the 95% that is actually wanted at two in the morning:
// journalctl, systemctl status, ls, cat, df. Those it does well.
//
// A real interactive shell needs a PTY behind a WebSocket on the device, which
// is a different piece of work and means every Pi in the fleet carries a
// listening shell endpoint. Worth doing deliberately or not at all.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X, TerminalSquare } from 'lucide-react';
import { deviceCommandsApi, TerminalCommandStatus } from '../services/deviceCommandsApi';

interface DeviceTerminalProps {
  deviceId: number;
  deviceName: string;
  onClose: () => void;
}

interface Line {
  kind: 'command' | 'output' | 'error' | 'notice';
  text: string;
}

/**
 * Remove ANSI escape sequences.
 *
 * systemctl and journalctl colour their output, and those codes arrive here as
 * literal `ESC[0;32m` noise in the middle of every line. Stripping them is the
 * one thing a terminal emulator library would genuinely have done for us; it is
 * a regex, so it did not seem worth a dependency for it.
 */
const stripAnsi = (text: string): string =>
  // eslint-disable-next-line no-control-regex
  text.replace(/\[[0-9;?]*[ -/]*[@-~]/g, '');

const DeviceTerminal: React.FC<DeviceTerminalProps> = ({ deviceId, deviceName, onClose }) => {
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(true);

  // Command history, walked with the up and down arrows. Newest first.
  const history = useRef<string[]>([]);
  const historyPos = useRef<number>(-1);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const append = useCallback((kind: Line['kind'], text: string) => {
    setLines(prev => [...prev, { kind, text }]);
  }, []);

  // Open a session on mount and close it on the way out. The API ages inactive
  // sessions out after thirty minutes anyway, but leaving one open every time
  // somebody glances at a device would fill the table with litter.
  useEffect(() => {
    let cancelled = false;
    let openedId: number | null = null;

    (async () => {
      try {
        const session = await deviceCommandsApi.createTerminalSession(deviceId);
        if (cancelled) {
          deviceCommandsApi.closeTerminalSession(session.sessionId).catch(() => {});
          return;
        }
        openedId = session.sessionId;
        setSessionId(session.sessionId);
        append('notice', `Connected to ${deviceName} (session ${session.sessionId}).`);
        append('notice', 'Each command runs on its own — cd and exports do not carry over.');
      } catch (err) {
        if (!cancelled) {
          append('error', err instanceof Error ? err.message : 'Could not open a session');
        }
      } finally {
        if (!cancelled) {
          setStarting(false);
          inputRef.current?.focus();
        }
      }
    })();

    return () => {
      cancelled = true;
      if (openedId !== null) {
        deviceCommandsApi.closeTerminalSession(openedId).catch(() => {});
      }
    };
  }, [deviceId, deviceName, append]);

  // Keep the newest output in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, busy]);

  const run = async () => {
    const command = input.trim();
    if (!command || sessionId === null || busy) return;

    if (command === 'clear') {
      setLines([]);
      setInput('');
      return;
    }

    history.current = [command, ...history.current.filter(c => c !== command)].slice(0, 50);
    historyPos.current = -1;

    append('command', command);
    setInput('');
    setBusy(true);

    try {
      const issued = await deviceCommandsApi.sendTerminalCommand(sessionId, command);

      // The device gives itself 30 seconds, and picks work up on a poll that can
      // be 20 seconds apart. Waiting 90 covers both with room to spare, and a
      // command that has not come back by then is reported as still running
      // rather than as failed - because it may well be.
      const finished = await pollTerminalCommand(sessionId, issued.commandId, 90);

      if (!finished) {
        append('notice', 'No answer yet — the device may still be working, or may not be reachable.');
      } else {
        const output = stripAnsi(finished.output ?? '').replace(/\s+$/, '');
        if (output) append('output', output);
        if (finished.exitCode !== undefined && finished.exitCode !== 0) {
          append('error', `exit ${finished.exitCode}`);
        }
      }
    } catch (err) {
      append('error', err instanceof Error ? err.message : 'Command failed');
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  /**
   * Poll the session until this command reports back.
   *
   * The API returns every command in the session rather than one by id, so this
   * picks its own out of the list each time.
   */
  const pollTerminalCommand = async (
    session: number,
    commandId: number,
    attempts: number
  ): Promise<TerminalCommandStatus | null> => {
    for (let i = 0; i < attempts; i++) {
      const all = await deviceCommandsApi.getTerminalCommands(session);
      const mine = all.find(c => c.id === commandId);
      if (mine && mine.status === 'completed') return mine;
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    return null;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      run();
      return;
    }

    // Arrow keys walk the history, the way a shell does. Without it, retyping a
    // long journalctl invocation is the whole experience.
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (historyPos.current + 1 < history.current.length) {
        historyPos.current += 1;
        setInput(history.current[historyPos.current]);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyPos.current > 0) {
        historyPos.current -= 1;
        setInput(history.current[historyPos.current]);
      } else {
        historyPos.current = -1;
        setInput('');
      }
    }
  };

  const colourFor = (kind: Line['kind']): string => {
    switch (kind) {
      case 'command': return 'text-cyan-300';
      case 'error': return 'text-red-400';
      case 'notice': return 'text-gray-500';
      default: return 'text-gray-200';
    }
  };

  return (
    <div className="flex h-[32rem] flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-900">
      <div className="flex items-center gap-2 border-b border-gray-700 bg-gray-800 px-3 py-2">
        <TerminalSquare className="h-4 w-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-200">{deviceName}</span>
        {starting && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
        <button
          onClick={onClose}
          className="ml-auto rounded p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-200"
          aria-label="Close terminal"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-2 font-mono text-xs leading-relaxed"
        onClick={() => inputRef.current?.focus()}
      >
        {lines.map((line, i) => (
          <pre key={i} className={`whitespace-pre-wrap break-words ${colourFor(line.kind)}`}>
            {line.kind === 'command' ? `$ ${line.text}` : line.text}
          </pre>
        ))}

        {busy && (
          <div className="flex items-center gap-2 py-1 text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>waiting for the device...</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-gray-700 bg-gray-900 px-3 py-2 font-mono text-xs">
        <span className="text-cyan-300">$</span>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={starting || busy || sessionId === null}
          placeholder={sessionId === null ? 'no session' : 'systemctl status pinage-display'}
          spellCheck={false}
          autoComplete="off"
          className="flex-1 bg-transparent text-gray-100 placeholder-gray-600 outline-none disabled:opacity-50"
        />
      </div>
    </div>
  );
};

export default DeviceTerminal;
